import Component from '@glimmer/component'
import { reportingTitle } from 'teacher-ui-client/utils/giraffe/highcharts-config'
import { colors } from '@colors'
import BaseHighcharterComponent from 'teacher-ui-client/components/base/highcharter'
import type * as Highcharts from 'highcharts'

interface Args {
  categories?: string[]
  data: Highcharts.SeriesColumnrangeOptions[]
  dataLabelsEnabled?: boolean
  dataLabelsFormatter?: Highcharts.DataLabelsFormatterCallbackFunction
  toolTip: Highcharts.TooltipOptions
  xAxisLabel?: string
  yAxisFloor?: number
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

const disabled = { enabled: false }

export class SeriesColumnRange extends Component<Signature> {
  plotBackgroundColor = colors.dustyBlack50

  yAxisGridLineColor = colors.dustyBlack100

  get chartOptions(): Highcharts.Options {
    return {
      chart: {
        type: 'columnrange',
        plotBackgroundColor: this.plotBackgroundColor,
        backgroundColor: 'transparent',
      },
      credits: disabled,
      title: undefined,
      yAxis: this.yAxis,
      xAxis: this.xAxis,
      plotOptions: this.plotOptions,
      tooltip: this.args.toolTip,
      legend: this.legend,
    }
  }

  get dataLabelsEnabled() {
    return this.args.dataLabelsEnabled ?? false
  }

  get dataLabelsFormatter() {
    return this.args.dataLabelsFormatter
  }

  get highchartOptions(): Highcharts.Options {
    return { ...this.chartOptions, series: this.args.data }
  }

  get legend() {
    return disabled
  }

  get plotOptions(): Highcharts.PlotOptions {
    const options: Record<string, any> = {
      series: {
        dataLabels: {
          enabled: this.dataLabelsEnabled,
          style: {
            // Explicitly define this, because otherwise I encountered a highcharts bug where occasionally, some numbers
            // will have a different outline for no reason
            textOutline: 'none',
          },
          /* Allow datalabels to overflow the chart (and don't crop them). Normally, a datalabel when too close to the
           * edge of a chart, would wrap to the next line. This is usually fine, except in the case of a very small
           * column range, where it can look a bit odd - so instead just let it display as slightly outside of chart
           */
          overflow: 'allow',
          crop: false,
        },
      },
    }

    if (this.dataLabelsFormatter) {
      options.series.dataLabels.formatter = this.dataLabelsFormatter
    }

    return options
  }

  get xAxis() {
    return {
      title: reportingTitle(this.xAxisLabel),
      categories: this.args.categories,
      allowDecimals: false,
      lineColor: colors.dustyBlack200,
      tickColor: colors.dustyBlack200,
    }
  }

  get xAxisLabel() {
    return this.args.xAxisLabel ?? ''
  }

  get yAxis(): Highcharts.YAxisOptions {
    return {
      floor: this.yAxisFloor,
      gridLineColor: this.yAxisGridLineColor,
      title: undefined,
    }
  }

  get yAxisFloor() {
    return this.args.yAxisFloor ?? 0
  }

  <template><BaseHighcharterComponent class="h-full" @options={{this.highchartOptions}} ...attributes /></template>
}

export default SeriesColumnRange
